import { useCallback } from 'react';
import { CalendarEvent } from '../types';
import { useShiftApi } from './useShiftApi';
import { useUndoRedo } from './useUndoRedo';
import { supabase } from '@/integrations/supabase/client';

/**
 * Wrapper around useShiftApi that adds undo/redo functionality
 * @param refetch - Function to refresh the calendar
 * @param teamId - Current team ID (history is cleared when this changes)
 */
export const useShiftApiWithUndo = (refetch: () => void, teamId?: string | null) => {
  const shiftApi = useShiftApi();
  const undoRedo = useUndoRedo(refetch, teamId);

  const createShift = useCallback(async (shift: CalendarEvent): Promise<CalendarEvent | null> => {
    const result = await shiftApi.createShift(shift);
    
    if (result) {
      // Add to undo stack (non-blocking)
      undoRedo.addAction({
        type: 'create',
        timestamp: Date.now(),
        createdShift: result
      });
    }
    
    return result;
  }, [shiftApi.createShift, undoRedo.addAction]);

  const updateShift = useCallback(async (shiftId: string, updates: Partial<CalendarEvent>): Promise<CalendarEvent | null> => {
    // Start fetching old data immediately (in parallel)
    const oldShiftPromise = supabase
      .from('team_schedules')
      .select('*')
      .eq('id', shiftId)
      .single();

    // Execute both in parallel
    const [{ data: oldShift }, result] = await Promise.all([
      oldShiftPromise,
      shiftApi.updateShift(shiftId, updates)
    ]);
    
    // Add to undo stack if both succeeded
    if (result && oldShift) {
      undoRedo.addAction({
        type: 'update',
        timestamp: Date.now(),
        oldShift: oldShift as CalendarEvent,
        newShift: result
      });
    }
    
    return result;
  }, [shiftApi.updateShift, undoRedo.addAction]);

  const deleteShift = useCallback(async (shiftId: string, teamId?: string): Promise<boolean> => {
    // Start fetching shift data immediately (in parallel)
    const deletedShiftPromise = supabase
      .from('team_schedules')
      .select('*')
      .eq('id', shiftId)
      .single();

    // Execute both in parallel
    const [{ data: deletedShift }, result] = await Promise.all([
      deletedShiftPromise,
      shiftApi.deleteShift(shiftId, teamId)
    ]);
    
    // Add to undo stack if both succeeded
    if (result && deletedShift) {
      undoRedo.addAction({
        type: 'delete',
        timestamp: Date.now(),
        deletedShift: deletedShift as CalendarEvent
      });
    }
    
    return result;
  }, [shiftApi.deleteShift, undoRedo.addAction]);

  const deleteBulkShifts = useCallback(async (shiftIds: string[], teamId?: string): Promise<boolean> => {
    // Start fetching all shifts immediately (in parallel)
    const deletedShiftsPromise = supabase
      .from('team_schedules')
      .select('*')
      .in('id', shiftIds);

    // Execute both in parallel
    const [{ data: deletedShifts }, result] = await Promise.all([
      deletedShiftsPromise,
      shiftApi.deleteBulkShifts(shiftIds, teamId)
    ]);
    
    // Add to undo stack if both succeeded
    if (result && deletedShifts && deletedShifts.length > 0) {
      undoRedo.addAction({
        type: 'bulk_delete',
        timestamp: Date.now(),
        deletedShifts: deletedShifts as CalendarEvent[]
      });
    }
    
    return result;
  }, [shiftApi.deleteBulkShifts, undoRedo.addAction]);

  return {
    createShift,
    updateShift,
    deleteShift,
    deleteBulkShifts,
    isLoading: shiftApi.isLoading,
    // Expose undo/redo functionality
    undo: undoRedo.undo,
    redo: undoRedo.redo,
    canUndo: undoRedo.canUndo,
    canRedo: undoRedo.canRedo,
    clearHistory: undoRedo.clearHistory
  };
};
